import { useState, useEffect, useCallback } from 'react';
import { getApps, type App, type AppsMeta } from '../services/appsService';

export function useApps({ perPage }: { perPage?: number } = {}) {
  const [apps, setApps] = useState<App[]>([]);
  const [meta, setMeta] = useState<AppsMeta | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchPage = useCallback(async (page: number, search: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const { apps: data, meta: m } = await getApps(page, search, perPage);
      setApps(data);
      setMeta(m);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load apps');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(1, debouncedSearch);
  }, [debouncedSearch, fetchPage]);

  const retry = useCallback(() => {
    fetchPage(meta?.current_page ?? 1, debouncedSearch);
  }, [fetchPage, meta, debouncedSearch]);

  const goToPage = useCallback(
    (page: number) => {
      setSearchQuery('');
      setDebouncedSearch('');
      fetchPage(page, '');
    },
    [fetchPage]
  );

  return {
    apps,
    meta,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    retry,
    goToPage
  };
}
